import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  User,
  Gamepad,
  Bot,
  Server,
  MonitorSmartphone
} from "lucide-react";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileActiveDropdown, setMobileActiveDropdown] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const navLinks = [
    {
      name: "Game Servers",
      href: "games",
      icon: Gamepad,
    },
    {
      name: "Discord",
      href: "/discord",
      icon: Bot,
    },
    { name: "Web",
      href: "/website", 
      icon: MonitorSmartphone 
    },
    {
      name: "Dedicated",
      href: "/dedicated",
      icon: Server,
    },
    {
      name: "VPS",
      href: "#",
      icon: Server,
      dropdown: [
        { name: "Intel Xeon VPS", href: "/intel-xeon" },
        { name: "AMD Ryzen VPS", href: "/ryzen-vps" },
        { name: "Storage", href: "/storage" },
      ],
    },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsOpen(false);
    setMobileActiveDropdown(null);
  };

  return (
    <>
      {/* Overlay for mobile menu */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/80 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <nav
        className={`fixed w-full top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-gray-900 border-b border-gray-800"
            : "bg-gray-900 border-gray-800"
        }`}
      >

        {/* Main Navbar */}
        <div className="px-4 mx-auto max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
              {/* Logo Image */}
              <img 
                src="/images/rave.png" 
                alt="RaveNodes Logo" 
                className="w-12 h-15"
              />
                <span className="text-3xl font-bold text-transparent bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text">
                  RaveNodes
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="items-center hidden space-x-2 md:flex">
              {navLinks.map((link) => (
                <div key={link.name} className="relative group">
                  <button
                    onClick={() => handleNavigation(link.href)}
                    className="flex items-center px-4 py-2 space-x-2 font-bold text-gray-300 transition-all rounded-lg group-hover:text-white group-hover:bg-gray-800"
                  >
                    {link.icon && <link.icon size={16} />}
                    <span>{link.name}</span>
                    {link.dropdown && (
                      <ChevronDown
                        size={14}
                        className="transition-transform duration-200 transform group-hover:rotate-180"
                      />
                    )}
                  </button>

                  {link.dropdown && (
                    <div
                      className="absolute left-0 invisible py-2 mt-1 transition-all duration-200 transform translate-y-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl opacity-0 top-full w-52 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0"
                    >
                      <div className="absolute w-full h-4 bg-transparent -top-4"></div>
                      {link.dropdown.map((item) => (
                        <button
                          key={item.name}
                          onClick={() => handleNavigation(item.href)}
                          className="block w-full px-4 py-2 font-bold text-left text-gray-300 transition-all hover:bg-gray-700 hover:text-white"
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Action Buttons */}
              <div className="flex items-center ml-4 space-x-2">
                <a
                  href="https://billing.ravenodes.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center px-6 py-2 font-medium text-white transition-all rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 group"
                >
                  <User
                    size={16}
                    className="mr-2 transition-transform transform group-hover:scale-110"
                  />
                  Client Area
                </a>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 text-gray-300 transition-all rounded-lg hover:text-white hover:bg-gray-800"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          <div
            className={`fixed inset-y-0 right-0 w-full max-w-sm bg-gray-900 shadow-2xl transition-transform duration-300 ease-in-out md:hidden ${
              isOpen ? "translate-x-1" : "translate-x-full"
            }`}
            style={{
              top: 0,
              bottom: 0,
              zIndex: 999,
            }}
          >
            {/* Mobile Menu Header */}
            <div className="sticky top-0 flex items-center justify-between p-4 bg-gray-900 border-b border-gray-800">
              <span className="text-xl font-bold text-white">Menu</span>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-gray-300 transition-all rounded-lg hover:text-white hover:bg-gray-800"
              >
                <X size={24} />
              </button>
            </div>

            {/* Mobile Menu Content */}
            <div className="overflow-y-auto h-[calc(100vh-4rem)]">

              {/* Main Navigation */}
              <div className="p-4 space-y-2">
                {navLinks.map((link) => (
                  <div key={link.name}>
                    <button
                      className="flex items-center justify-between w-full p-3 text-left text-gray-100 transition-all rounded-lg hover:text-white hover:bg-gray-800"
                      onClick={() => {
                        if (link.dropdown) {
                          setMobileActiveDropdown(
                            mobileActiveDropdown === link.name
                              ? null
                              : link.name
                          );
                        } else {
                          handleNavigation(link.href);
                        }
                      }}
                    >
                      <span className="flex items-center space-x-3">
                        {link.icon && (
                          <link.icon size={20} className="text-blue-400" />
                        )}
                        <span className="font-medium">{link.name}</span>
                      </span>
                      {link.dropdown && (
                        <ChevronRight
                          size={20}
                          className={`transform transition-transform duration-200 ${
                            mobileActiveDropdown === link.name
                              ? "rotate-90"
                              : ""
                          }`}
                        />
                      )}
                    </button>

                    {link.dropdown && mobileActiveDropdown === link.name && (
                      <div className="pl-4 mt-1 border-l-2 ml-9 border-blue-500/20">
                        {link.dropdown.map((item) => (
                          <button
                            key={item.name}
                            onClick={() => handleNavigation(item.href)}
                            className="block w-full p-3 text-left text-gray-300 transition-all rounded-lg hover:text-white hover:bg-gray-800"
                          >
                            {item.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Client Area Button */}
              <div className="p-4 bg-gray-900 border-t border-gray-800">
                <a
                  href="https://billing.ravenodes.com"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center w-full p-4 space-x-2 font-medium text-white transition-all rounded-lg shadow-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <User size={18} />
                  <span>Client Area</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
